/**
 * [T270-PY] DayOfWeekHeatmap — CSS-Grid 7×N heatmap for the redesigned
 * Day-of-Week tab. Each cell is a `<button>` that drill-throughs when activated.
 *
 * Contract authority: `docs/superpowers/specs/2026-08-03-day-of-week-insights-design.md`
 *
 * Critical invariants (unchanged from the v3.10 contract):
 *   - CSS Grid primitive, NOT a `<table>`. The grid is wrapped in a local
 *     horizontal scrollport (`overflow-x-auto overflow-y-hidden overscroll-x-contain
 *     scrollbar-none`) so day columns scroll inside the panel while the document
 *     remains at zero horizontal overflow.
 *   - Each cell is a real `<button type="button">`.
 *   - Two-axis arrow-key roving tabindex (WCAG 2.1.1 + 2.1.2).
 *     Tab exits the grid; ArrowLeft/Right/Up/Down navigate cells;
 *     Home/End jump to row edges; Ctrl+Home/End jump to grid corners;
 *     Escape returns focus to the grid container.
 *   - Cell intensity uses per-cell totals (NOT row sums) so "concentrated on
 *     Friday" categories show a strong Friday cell.
 *   - Cell background + foreground are SOLID composited RGB values derived from
 *     the neutral data-token and the card surface. NO whole-element opacity is
 *     applied to the cell, the KPI, or any ancestor.
 *   - The Category header and every row label are pinned to the scrollport's
 *     left edge with `position: sticky` and an opaque `bg-card` surface.
 *   - Foreground color is chosen by `selectReadableForeground` so the displayed
 *     value meets the WCAG 4.5:1 contrast ratio against the composited cell
 *     background in both light and dark themes.
 *
 * @module transactions/DayOfWeekHeatmap
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  composeHeatmapCell,
  currencyIntensity,
  resolveHeatmapPalette,
  rgbToCss,
  type RgbColor,
} from '@/lib/heatmapContrast';
import type { DayOfWeekBucket } from '@/lib/insightsContracts';

/** Resolved per-day view — index 0..6 maps to the day-name array. */
export interface DayOfWeekHeatmapDay {
  /** 1 (Sun) … 7 (Sat) — Mongo `$dayOfWeek` convention. */
  dayOfWeek: number;
  dayName: string;
}

export interface DayOfWeekHeatmapCell {
  /** `total` spent in this (day, category) bucket. */
  total: number;
  /** `count` of transactions in this (day, category) bucket. */
  count: number;
}

export interface DayOfWeekHeatmapCategoryRow {
  categoryId: string;
  name: string;
  emoji: string;
  /** 7 entries — one per heatmap day, index-aligned with `days`. */
  perDayTotals: number[];
  /** 7 entries — index-aligned. */
  perDayCounts: number[];
  /** Sum of `perDayTotals` (used by the legend's row label, NOT for opacity). */
  rowTotal: number;
  /** True only for the synthetic `__other__` bucket. */
  isOther: boolean;
}

/** Drill-through callback — mirrors contract §2.2 / §5.3. */
export type DayOfWeekCellDrillInput = {
  kind: 'cell';
  dayOfWeek: number;
  categoryId: string;
  windowStart: string;
  windowEnd: string;
};

export interface DayOfWeekHeatmapProps {
  /** 7-entry array (Sun→Sat OR Mon→Sun — caller controls order). */
  days: DayOfWeekHeatmapDay[];
  /** Pre-computed rows from `buildCategoryRows`. */
  rows: DayOfWeekHeatmapCategoryRow[];
  /** Pre-computed max over `r × d` of `perDayTotals[d]` (contract §4.2). */
  maxCellTotal: number;
  /** Today's dayOfWeek — highlights the column + adds `aria-current="date"`. */
  currentDow: number;
  /** Currency formatter from `useCurrency().fmt`. */
  fmt: (n: number) => string;
  /** When undefined, every cell renders `disabled=true`. */
  /** ISO 4217 currency code (e.g. 'IDR', 'USD'). Used by the per-currency intensity bins. */
  currency?: string;
  buildDrillHref?: (input: DayOfWeekCellDrillInput) => string;
  /** ISO date — start of the day-of-week window. */
  windowStart: string;
  /** ISO date — end of the day-of-week window. */
  windowEnd: string;
  /** Stable testid prefix; rows/cells append `-row-{id}` / `-cell-{id}-{dow}`. */
  testIdPrefix?: string;
}

const DEFAULT_TESTID_PREFIX = 'day-of-week-redesign-heatmap';
const ROW_LABEL_COL = 'Category';

/** Grid template — pinned Category column + 7 fixed 96px day columns (FE-1 / UX-1 §10.3).
 *  The 96px width is content-fit: the widest formatted amount in the seeded
 *  fixture (`$12,000.00` = 80.08px at 16px Inter) plus 4px padding plus 12px
 *  headroom for locale-specific currency formatting (e.g. `€12.000,00`) is
 *  ~96px. The 44px touch target floor is preserved on the cell `<button>` via
 *  `min-h-[44px]`; the button shape is NOT `aspect-square` so the column width
 *  is the only X-axis driver and the cell stays 96 × 44. */
const DAY_COLUMN_TEMPLATE = 'repeat(7,96px)';

/** Foreground candidates used by `selectReadableForeground`. */
const FOREGROUND_CANDIDATES: readonly RgbColor[] = [
  [0, 0, 0],
  [255, 255, 255],
];

/** WCAG 2.2 normal-text minimum contrast ratio. */
const MINIMUM_CONTRAST_RATIO = 4.5;
/**
 * Observe the `dark` class on `document.documentElement` so the heatmap
 * recomputes its composited cell colors when the theme changes.
 */
function useThemeMode(): 'light' | 'dark' {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof document === 'undefined') return 'light';
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  });
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const target = document.documentElement;
    const sync = () => {
      setTheme(target.classList.contains('dark') ? 'dark' : 'light');
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(target, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return theme;
}

/**
 * Map a `(rowIdx, colIdx)` pair onto a clamped `(row, col)` so arrow-key
 * navigation never moves focus off the grid.
 */
function clampCell(
  row: number,
  col: number,
  rowCount: number,
  colCount: number,
): { row: number; col: number } {
  return {
    row: Math.max(0, Math.min(rowCount - 1, row)),
    col: Math.max(0, Math.min(colCount - 1, col)),
  };
}

export function DayOfWeekHeatmap({
  days,
  rows,
  // maxCellTotal is no longer used (intensity is currency-binned) but kept in the
  // public API for backwards compatibility.
  maxCellTotal: _maxCellTotal,
  currentDow,
  fmt,
  currency = 'USD',
  buildDrillHref,
  windowStart,
  windowEnd,
  testIdPrefix = DEFAULT_TESTID_PREFIX,
}: DayOfWeekHeatmapProps) {
  const theme = useThemeMode();
  const palette = useMemo(() => resolveHeatmapPalette(theme), [theme]);
  // Two-axis roving tabindex. `null` means no cell has been focused yet.
  const [activeCell, setActiveCell] = useState<{ row: number; col: number } | null>(
    null,
  );
  const gridRef = useRef<HTMLDivElement | null>(null);
  // Stable flat ref list — one entry per row (each row is a 7-cell list).
  const cellRefs = useRef<Array<Array<HTMLButtonElement | null>>>([]);

  const moveFocus = useCallback(
    (row: number, col: number) => {
      const next = clampCell(row, col, rows.length, days.length);
      setActiveCell(next);
      // Focus the underlying button after the React re-render flushes.
      requestAnimationFrame(() => {
        cellRefs.current[next.row]?.[next.col]?.focus();
      });
    },
    [rows.length, days.length],
  );

  const onCellKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, rowIdx: number, colIdx: number) => {
      const lastRow = rows.length - 1;
      const lastCol = days.length - 1;
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          moveFocus(rowIdx, colIdx + 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          moveFocus(rowIdx, colIdx - 1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          moveFocus(rowIdx + 1, colIdx);
          break;
        case 'ArrowUp':
          e.preventDefault();
          moveFocus(rowIdx - 1, colIdx);
          break;
        case 'Home':
          e.preventDefault();
          if (e.ctrlKey) {
            moveFocus(0, 0);
          } else {
            moveFocus(rowIdx, 0);
          }
          break;
        case 'End':
          e.preventDefault();
          if (e.ctrlKey) {
            moveFocus(lastRow, lastCol);
          } else {
            moveFocus(rowIdx, lastCol);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setActiveCell(null);
          gridRef.current?.focus();
          break;
        default:
          break;
      }
    },
    [days.length, moveFocus, rows.length],
  );

  const isDisabled = !buildDrillHref;
  const onActivate = useCallback(
    (categoryId: string, dayOfWeek: number) => {
      if (!buildDrillHref) return;
      try {
        const href = buildDrillHref({
          kind: 'cell',
          dayOfWeek,
          categoryId,
          windowStart,
          windowEnd,
        });
        if (href) {
          window.location.assign(href);
        }
      } catch {
        // Fallback per contract §2.2: never let drill breakage block the UI.
        window.location.assign('/transactions');
      }
    },
    [buildDrillHref, windowEnd, windowStart],
  );

  // Memoize the cell index → flat position for the per-day header.
  const dayHeaders = useMemo(
    () =>
      days.map((d) => ({
        ...d,
        isToday: d.dayOfWeek === currentDow,
      })),
    [days, currentDow],
  );

  // Ensure the cellRefs slot table is dense.
  if (cellRefs.current.length !== rows.length) {
    cellRefs.current = rows.map(
      (_, rIdx) => cellRefs.current[rIdx] ?? Array(days.length).fill(null),
    );
  }

  // [T270PY-FE-1] `isolate` establishes a lower stacking context so the

  // internal sticky z-20 (category header) / z-10 (row labels) order is

  // preserved for horizontal scrolling but the entire heatmap paints

  // BELOW the UnifiedTopBar's z-[8] in the parent context during vertical

  // page scroll. Without isolation, the labels' z-20 would escape and

  // paint over the floating transaction filter bar.

  return (

    <div

      data-testid={`${testIdPrefix}-scrollport`}

      className="relative z-0 isolate scrollbar-none overflow-x-auto overflow-y-hidden overscroll-x-contain"
      >


      <div
        ref={gridRef}
        role="grid"
        tabIndex={0}
        aria-label="Spending by day of week across top 5 categories plus Other"
        data-testid={`${testIdPrefix}-grid`}
        className="grid w-max min-w-max grid-cols-[120px_repeat(7,96px)] gap-px rounded-md bg-border"
        style={{ gridTemplateColumns: `minmax(120px, auto) ${DAY_COLUMN_TEMPLATE}` }}
      >
      {/* Header row */}
      <div role="row" className="contents">
        <div
          role="columnheader"
          data-testid={`${testIdPrefix}-category-header`}
          className="sticky left-0 z-20 flex min-h-[44px] min-w-[120px] items-center bg-card p-1 text-xs font-medium text-muted-foreground"
        >
          {ROW_LABEL_COL}
        </div>
        {dayHeaders.map((d) => (
          <div
            key={`header-${d.dayOfWeek}`}
            role="columnheader"
            data-testid={`${testIdPrefix}-col-${d.dayOfWeek}`}
            aria-current={d.isToday ? 'date' : undefined}
            className={
              `flex min-h-[44px] min-w-[44px] items-center justify-center bg-card p-1 text-center ` +
              `text-xs text-muted-foreground tabular-nums ` +
              (d.isToday ? 'font-semibold text-foreground' : '')
            }
          >
            <span aria-hidden="true">{d.dayName.slice(0, 3)}</span>
            <span className="sr-only">
              {d.dayName}
              {d.isToday ? ' (today)' : ''}
            </span>
          </div>
        ))}
      </div>

      {/* Data rows */}
      {rows.map((row, rowIdx) => (
        <div key={`row-${row.categoryId}`} role="row" className="contents">
          <div
            role="rowheader"
            data-testid={`${testIdPrefix}-row-${row.categoryId}`}
            className="sticky left-0 z-10 flex min-h-[44px] min-w-[120px] items-center bg-card p-1 text-xs text-muted-foreground"
          >
            <span className="break-words" title={row.name}>
              {row.emoji ? `${row.emoji} ` : ''}
              {row.name}
            </span>
          </div>
          {days.map((d, colIdx) => {
            const cellTotal = row.perDayTotals[colIdx] ?? 0;
            const cellCount = row.perDayCounts[colIdx] ?? 0;
            const isEmpty = cellTotal <= 0;
            const intensity = isEmpty ? 0 : currencyIntensity(cellTotal, currency);
            const { background, foreground } = composeHeatmapCell(
              palette,
              intensity,
              FOREGROUND_CANDIDATES,
              MINIMUM_CONTRAST_RATIO,
            );
            const isFocused =
              activeCell?.row === rowIdx && activeCell?.col === colIdx;
            return (
              <button
                key={`cell-${row.categoryId}-${d.dayOfWeek}`}
                ref={(el) => {
                  if (!cellRefs.current[rowIdx]) {
                    cellRefs.current[rowIdx] = [];
                  }
                  cellRefs.current[rowIdx][colIdx] = el;
                }}
                type="button"
                role="gridcell"
                tabIndex={isFocused ? 0 : -1}
                data-testid={`${testIdPrefix}-cell-${row.categoryId}-${d.dayOfWeek}`}
                aria-label={
                  isEmpty
                    ? `${d.dayName}, ${row.name}, no transactions.`
                    : `${d.dayName}, ${row.name}, ${fmt(cellTotal)}, ${cellCount} ${
                        cellCount === 1 ? 'transaction' : 'transactions'
                      }. Activate to view.`
                }
                disabled={isDisabled}
                onClick={() => onActivate(row.categoryId, d.dayOfWeek)}
                onKeyDown={(e) => onCellKeyDown(e, rowIdx, colIdx)}
                style={
                  isEmpty
                    ? {
                        backgroundColor: rgbToCss(palette.card),
                        color: rgbToCss(palette.card),
                      }
                    : {
                        backgroundColor: rgbToCss(background),
                        color: rgbToCss(foreground),
                      }
                }
                className={cn(
                  'flex min-h-[44px] min-w-[44px] items-center justify-center border border-border/60',
                  isEmpty && 'opacity-60',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'block w-full text-center text-[12px] font-medium tabular-nums tracking-tighter whitespace-nowrap px-1',
                    isEmpty && 'text-muted-foreground/40',
                  )}
                >
                  {isEmpty ? '—' : fmt(cellTotal)}
                </span>
              </button>
            );
          })}
        </div>
      ))}
      </div>
    </div>
  );
}

/**
 * Build the `CategoryRow[]` used by the heatmap AND by the legend.
 * Implementation mirrors design spec §4.1 — stores per-day totals for the
 * `__other__` row instead of a single scalar.
 */
export function buildCategoryRows(
  days: DayOfWeekBucket[],
  topN = 5,
): DayOfWeekHeatmapCategoryRow[] {
  // 1. Aggregate per-(day, categoryId) totals across the 7 days.
  interface Acc {
    name: string;
    emoji: string;
    perDay: number[];
    perCount: number[];
  }
  const aggregate = new Map<string, Acc>();
  const dayToCol = new Map<number, number>();
  days.forEach((d, i) => {
    dayToCol.set(d.dayOfWeek, i);
    for (const c of d.categories) {
      const key = c.categoryId || `name:${c.name}`;
      const acc = aggregate.get(key) ?? {
        name: c.name,
        emoji: c.emoji,
        perDay: Array(7).fill(0),
        perCount: Array(7).fill(0),
      };
      acc.perDay[i] = c.total;
      acc.perCount[i] = c.count;
      aggregate.set(key, acc);
    }
  });

  // 2. Compute row totals and rank.
  const ranked = Array.from(aggregate.entries())
    .map(([categoryId, acc]) => ({
      categoryId,
      name: acc.name,
      emoji: acc.emoji,
      perDayTotals: acc.perDay,
      perDayCounts: acc.perCount,
      rowTotal: acc.perDay.reduce((a, b) => a + b, 0),
      isOther: false,
    }))
    .sort((a, b) => b.rowTotal - a.rowTotal);

  // 3. Split top-N vs overflow → Other bucket.
  const top = ranked.slice(0, topN);
  const overflow = ranked.slice(topN);

  // 4. Per-day Other totals derived PER-DAY, not summed globally.
  const otherPerDay = Array(7).fill(0);
  for (const o of overflow) {
    for (let i = 0; i < 7; i += 1) otherPerDay[i] += o.perDayTotals[i];
  }
  const otherRowTotal = otherPerDay.reduce((a, b) => a + b, 0);

  return overflow.length > 0
    ? [
        ...top,
        {
          categoryId: '__other__',
          name: 'Other',
          emoji: '',
          perDayTotals: otherPerDay,
          perDayCounts: Array(7).fill(0),
          rowTotal: otherRowTotal,
          isOther: true,
        },
      ]
    : top;
}

/**
 * Cell max — the correct denominator (defect #3 fix).
 * Max over per-(row, col) cells, NOT over row sums.
 */
export function computeMaxCellTotal(rows: DayOfWeekHeatmapCategoryRow[]): number {
  let m = 0;
  for (const r of rows) {
    for (const v of r.perDayTotals) {
      if (v > m) m = v;
    }
  }
  return m;
}

export default DayOfWeekHeatmap;
